import type { GameEvent, MapDef, PlayerId } from './index.js'
import { attackDiceSides, defendDiceSides } from './gameLogic.js'
import { rollOutcomes } from './diceOdds.js'

export const DICE_LUCK_PERCENTILE_MIN_ROLLS = 20

export type DiceLuckVerdict = 'brutal' | 'unlucky' | 'average' | 'lucky' | 'blessed'

export interface DiceLuckRollDetail {
  eventIndex: number
  ts: number
  role: 'attack' | 'defense'
  fromTerritoryId: string
  toTerritoryId: string
  swing: number
  outcomeProbability: number
  attackerLost: number
  defenderLost: number
  attackerDice: number
  defenderDice: number
  attackerSides: number
  defenderSides: number
}

export interface DiceLuckPlayerSummary {
  playerId: PlayerId
  rollCount: number
  attackRollCount: number
  defenseRollCount: number
  netSwing: number
  attackSwing: number
  defenseSwing: number
  variance: number
  zScore: number | null
  percentile: number | null
  verdict: DiceLuckVerdict | null
}

export interface DiceLuckPlayerResult extends DiceLuckPlayerSummary {
  /** Cumulative net army swing after each roll involving this player. */
  series: number[]
  /** Cumulative attacking swing aligned one-to-one with `series`. */
  attackSeries: number[]
  /** Cumulative defending swing aligned one-to-one with `series`. */
  defenseSeries: number[]
  /** Cumulative outcome variance aligned one-to-one with `series`. */
  varianceSeries: number[]
  /** Exact raw log coordinates for each point in `series`. */
  seriesEvents: Array<{ eventIndex: number; ts: number }>
  bestRoll: DiceLuckRollDetail | null
  worstRoll: DiceLuckRollDetail | null
}

export interface DiceLuckResult {
  players: DiceLuckPlayerResult[]
  skippedRolls: number
}

/** Durable aggregate stored with a completed-game snapshot. */
export interface DiceLuckSnapshot {
  players: DiceLuckPlayerSummary[]
}

/** One immutable completed-game contribution to a cross-game luck history. */
export interface DiceLuckHistoryGame {
  gameId: string
  completedAt: string
  rollCount: number
  netSwing: number
  zScore: number
  percentile: number
  verdict: DiceLuckVerdict | null
}

/** Snapshot-backed lifetime aggregate. Missing legacy games are never counted as zero. */
export interface DiceLuckHistory {
  gamesRecorded: number
  rollCount: number
  attackRollCount: number
  defenseRollCount: number
  netSwing: number
  attackSwing: number
  defenseSwing: number
  variance: number
  zScore: number | null
  percentile: number | null
  verdict: DiceLuckVerdict | null
  bestGame: DiceLuckHistoryGame | null
  worstGame: DiceLuckHistoryGame | null
}

export interface DiceLuckHistoryInput {
  gameId: string
  completedAt: string
  summary: DiceLuckPlayerSummary
}

interface MutablePlayerLuck {
  playerId: PlayerId
  rollCount: number
  attackRollCount: number
  defenseRollCount: number
  netSwing: number
  attackSwing: number
  defenseSwing: number
  variance: number
  series: number[]
  attackSeries: number[]
  defenseSeries: number[]
  varianceSeries: number[]
  seriesEvents: Array<{ eventIndex: number; ts: number }>
  bestRoll: DiceLuckRollDetail | null
  worstRoll: DiceLuckRollDetail | null
}

function normalCdf(value: number): number {
  // Abramowitz-Stegun 7.1.26. Accurate to ~7.5e-8 and plenty for a displayed
  // whole-number percentile.
  const sign = value < 0 ? -1 : 1
  const x = Math.abs(value) / Math.sqrt(2)
  const t = 1 / (1 + 0.3275911 * x)
  const erf = sign * (1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t
    - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x))
  return (1 + erf) / 2
}

export function diceLuckVerdict(zScore: number, rollCount: number): DiceLuckVerdict | null {
  if (rollCount < 1 || !Number.isFinite(zScore)) return null
  // Early samples only qualify for the three ordinary bands.
  const mature = rollCount >= DICE_LUCK_PERCENTILE_MIN_ROLLS
  if (zScore <= -1.645 && mature) return 'brutal'
  if (zScore < -0.675) return 'unlucky'
  if (zScore < 0.675) return 'average'
  if (zScore < 1.645 || !mature) return 'lucky'
  return 'blessed'
}

export function diceLuckPercentile(zScore: number, rollCount: number): number | null {
  if (rollCount < DICE_LUCK_PERCENTILE_MIN_ROLLS) return null
  return normalCdf(zScore) * 100
}

function createPlayer(playerId: PlayerId): MutablePlayerLuck {
  return {
    playerId,
    rollCount: 0,
    attackRollCount: 0,
    defenseRollCount: 0,
    netSwing: 0,
    attackSwing: 0,
    defenseSwing: 0,
    variance: 0,
    series: [],
    attackSeries: [],
    defenseSeries: [],
    varianceSeries: [],
    seriesEvents: [],
    bestRoll: null,
    worstRoll: null,
  }
}

function addRoll(
  player: MutablePlayerLuck,
  role: 'attack' | 'defense',
  swing: number,
  variance: number,
  detail: DiceLuckRollDetail,
): void {
  player.rollCount++
  if (role === 'attack') {
    player.attackRollCount++
    player.attackSwing += swing
  } else {
    player.defenseRollCount++
    player.defenseSwing += swing
  }
  player.netSwing += swing
  player.variance += variance
  player.series.push(player.netSwing)
  player.attackSeries.push(player.attackSwing)
  player.defenseSeries.push(player.defenseSwing)
  player.varianceSeries.push(player.variance)
  player.seriesEvents.push({ eventIndex: detail.eventIndex, ts: detail.ts })
  if (!player.bestRoll || swing > player.bestRoll.swing) player.bestRoll = detail
  if (!player.worstRoll || swing < player.worstRoll.swing) player.worstRoll = detail
}

/**
 * Compute per-player dice luck from the authoritative attack-event sequence.
 * Neutral defenders (`defender === ''`) are intentionally not exposed as a
 * player, so neutral battles need not sum to zero across displayed players.
 */
export function computeDiceLuck(
  events: readonly GameEvent[],
  map: MapDef,
  playerIds: readonly PlayerId[] = [],
): DiceLuckResult {
  const byPlayer = new Map<PlayerId, MutablePlayerLuck>()
  for (const playerId of playerIds) byPlayer.set(playerId, createPlayer(playerId))
  const territoryById = new Map(map.territories.map((territory) => [territory.id, territory]))
  let skippedRolls = 0

  const player = (playerId: PlayerId): MutablePlayerLuck => {
    let existing = byPlayer.get(playerId)
    if (!existing) {
      existing = createPlayer(playerId)
      byPlayer.set(playerId, existing)
    }
    return existing
  }

  events.forEach((event, eventIndex) => {
    if (event.kind !== 'attack') return
    const from = territoryById.get(event.fromTerritoryId)
    const to = territoryById.get(event.toTerritoryId)
    const attackerDice = event.roll.attacker.length
    const defenderDice = event.roll.defender.length
    if (!from || !to || attackerDice < 1 || defenderDice < 1) {
      skippedRolls++
      return
    }

    const attackerSides = attackDiceSides(from, to.id)
    const defenderSides = defendDiceSides(to, from.id)
    const outcomes = rollOutcomes(attackerDice, defenderDice, attackerSides, defenderSides)
    const expectedNet = outcomes.reduce(
      (sum, [probability, attackerLost, defenderLost]) =>
        sum + probability * (defenderLost - attackerLost),
      0,
    )
    const variance = outcomes.reduce((sum, [probability, attackerLost, defenderLost]) => {
      const delta = defenderLost - attackerLost - expectedNet
      return sum + probability * delta * delta
    }, 0)
    const actualNet = event.defenderLost - event.attackerLost
    const attackerSwing = actualNet - expectedNet
    const outcomeProbability = outcomes.reduce(
      (sum, [probability, attackerLost, defenderLost]) =>
        attackerLost === event.attackerLost && defenderLost === event.defenderLost
          ? sum + probability
          : sum,
      0,
    )
    const baseDetail = {
      eventIndex,
      ts: event.ts,
      fromTerritoryId: event.fromTerritoryId,
      toTerritoryId: event.toTerritoryId,
      outcomeProbability,
      attackerLost: event.attackerLost,
      defenderLost: event.defenderLost,
      attackerDice,
      defenderDice,
      attackerSides,
      defenderSides,
    }

    addRoll(player(event.attacker), 'attack', attackerSwing, variance, {
      ...baseDetail,
      role: 'attack',
      swing: attackerSwing,
    })
    if (event.defender !== '') {
      addRoll(player(event.defender), 'defense', -attackerSwing, variance, {
        ...baseDetail,
        role: 'defense',
        swing: -attackerSwing,
      })
    }
  })

  const players = [...byPlayer.values()].map((value): DiceLuckPlayerResult => {
    const zScore = value.variance > 0 ? value.netSwing / Math.sqrt(value.variance) : null
    return {
      ...value,
      zScore,
      percentile: zScore === null ? null : diceLuckPercentile(zScore, value.rollCount),
      verdict: zScore === null ? null : diceLuckVerdict(zScore, value.rollCount),
    }
  })

  return { players, skippedRolls }
}

export function diceLuckSnapshot(result: DiceLuckResult): DiceLuckSnapshot {
  return {
    players: result.players.map(({
      series: _series,
      attackSeries: _attackSeries,
      defenseSeries: _defenseSeries,
      varianceSeries: _varianceSeries,
      seriesEvents: _seriesEvents,
      bestRoll: _best,
      worstRoll: _worst,
      ...summary
    }) => summary),
  }
}

/**
 * Combine immutable per-game summaries without averaging their z-scores.
 * Independent roll variances add, so the lifetime z-score is the total swing
 * divided by the square root of total variance. Best/worst games use their
 * per-game z-score, which keeps games with different roll counts comparable.
 */
export function aggregateDiceLuckHistory(
  games: readonly DiceLuckHistoryInput[],
): DiceLuckHistory {
  const totals = games.reduce((value, game) => ({
    rollCount: value.rollCount + game.summary.rollCount,
    attackRollCount: value.attackRollCount + game.summary.attackRollCount,
    defenseRollCount: value.defenseRollCount + game.summary.defenseRollCount,
    netSwing: value.netSwing + game.summary.netSwing,
    attackSwing: value.attackSwing + game.summary.attackSwing,
    defenseSwing: value.defenseSwing + game.summary.defenseSwing,
    variance: value.variance + game.summary.variance,
  }), {
    rollCount: 0,
    attackRollCount: 0,
    defenseRollCount: 0,
    netSwing: 0,
    attackSwing: 0,
    defenseSwing: 0,
    variance: 0,
  })
  const zScore = totals.variance > 0
    ? totals.netSwing / Math.sqrt(totals.variance)
    : null
  const scoredGames = games
    .filter((game) => game.summary.zScore !== null && game.summary.variance > 0)
    .map((game): DiceLuckHistoryGame => ({
      gameId: game.gameId,
      completedAt: game.completedAt,
      rollCount: game.summary.rollCount,
      netSwing: game.summary.netSwing,
      zScore: game.summary.zScore!,
      percentile: normalCdf(game.summary.zScore!) * 100,
      verdict: diceLuckVerdict(game.summary.zScore!, game.summary.rollCount),
    }))
    .sort((a, b) => a.zScore - b.zScore || a.netSwing - b.netSwing ||
      a.completedAt.localeCompare(b.completedAt))

  return {
    gamesRecorded: games.length,
    ...totals,
    zScore,
    percentile: zScore === null ? null : diceLuckPercentile(zScore, totals.rollCount),
    verdict: zScore === null ? null : diceLuckVerdict(zScore, totals.rollCount),
    bestGame: scoredGames.at(-1) ?? null,
    worstGame: scoredGames[0] ?? null,
  }
}

/** Current public identity of a leaderboard entry. */
export interface DiceLuckLeaderboardPlayer {
  userId: string
  username: string
  displayName: string
}

/** One player's result in one completed ranked game. */
export interface DiceLuckLeaderboardGame extends DiceLuckHistoryGame {
  gameName: string | null
  player: DiceLuckLeaderboardPlayer
}

/** One player's lifetime ranked-game luck. */
export interface DiceLuckLeaderboardOverall {
  player: DiceLuckLeaderboardPlayer
  gamesRecorded: number
  rollCount: number
  netSwing: number
  zScore: number
  percentile: number | null
  verdict: DiceLuckVerdict | null
}

/**
 * GET /api/v1/leaderboard/dice-luck. Humans in ranked games only, ranked by
 * z-score like the profile best/worst cards. Thresholds keep tiny samples from
 * topping the board.
 */
export interface DiceLuckLeaderboardResponse {
  /** Rolls a game, or a player's ranked lifetime, needs to hold a record. */
  minRolls: number
  luckiestGame: DiceLuckLeaderboardGame | null
  unluckiestGame: DiceLuckLeaderboardGame | null
  luckiestPlayer: DiceLuckLeaderboardOverall | null
  unluckiestPlayer: DiceLuckLeaderboardOverall | null
}
